import React, { useEffect, useRef, useImperativeHandle, useCallback, forwardRef, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';

import hash from 'hash-sum';
import AntPage from './AntPage';
import AntToolbar from './AntToolbar';
import AntLeftSidebar from './AntLeftSidebar';
import AntSearchForm from './AntSearchForm';
import AntSearchGrid from './AntSearchGrid';
import AntSearchPivotGrid from './AntSearchPivotGrid';
import AntMessageBox from './AntMessageBox';
import AntBatchExecutePopup from './AntBatchExecutePopup';
import AntErrorPage from './AntErrorPage';
// import AntErrorPanel from './AntErrorPanel';
import AntCondition from '../utils/AntCondition';
import AntConfig from '../utils/AntConfig';
import AntAuth from '../utils/AntAuth';
import AntLocalization from '../utils/AntLocalization';
import AntAPI from '../utils/AntAPI';
import { resolveNavigateLink } from '../utils/AntQuickCreate';
// import AntAccordion from './AntAccordion';
import AntImport from './AntImport';
import { useGridSettingsScope } from './AntGridScope';
import { buildGridSettingsKey, readGridSettings, updateGridSettings, takeLegacyGridEntry } from '../utils/AntGridSettings';

import { AntGlobal, AntSession, AntSidebarContext } from '../utils/AntContext';

const DEFAULT_PAGE_SIZES = [15, 30, 50];
const DEFAULT_DETAIL_PAGE_SIZES = [30, 50, 100];

export default forwardRef(function (props, ref) {
    const canRender = useMemo(() => {
        let _canRender = AntCondition.isValid(null, {
            whenSession: {
                session_status: { type: '!=', value: 'Restricted' }
            }
        });

        return _canRender && AntCondition.isRender(null, props);
    }, []);

    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    // Clear sidebar context when on search page
    var _sidebarCtx = React.useContext(AntSidebarContext);
    React.useEffect(function () {
        if (_sidebarCtx && _sidebarCtx.setContext) _sidebarCtx.setContext(null);
    }, []);

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    // Cấu hình màn danh sách (điều kiện form tìm kiếm, hiện form / chế độ chi tiết) trong object chung AntGridSettings:
    // khoá <phạm vi>/page:<bảng> — cùng quy tắc phạm vi với AntGrid (khung chứa, không có thì route pattern).
    const { scope: pageSettingsScope, route: pageSettingsRoute } = useGridSettingsScope();
    const pageSettingsName = String(props.name || tableName || '').replace(/[#/]/g, '.');
    const pageSettingsKey = buildGridSettingsKey(pageSettingsScope, 'page:' + pageSettingsName);
    const pageSettingsMeta = { scope: pageSettingsScope, route: pageSettingsRoute, grid: 'page:' + pageSettingsName, table: tableName || undefined };
    const pageSettingsMigrated = useRef(false);
    if (!pageSettingsMigrated.current) {
        pageSettingsMigrated.current = true;
        // Chuyển từ khoá cũ (băm theo pathname).
        const legacyHash = hash(String(location.pathname));
        const oldConds = takeLegacyGridEntry('search-conds', legacyHash);
        const oldStates = takeLegacyGridEntry('search-states', legacyHash);
        if (oldConds !== undefined || oldStates !== undefined)
            updateGridSettings(
                pageSettingsKey,
                (entry) => ({
                    ...entry,
                    searchForm: entry.searchForm || (oldConds && Object.keys(oldConds).length ? { conditions: oldConds } : null),
                    view:
                        entry.view ||
                        (oldStates && (oldStates.showSearchForm || oldStates.isDetailMode)
                            ? { ...(oldStates.showSearchForm ? { showSearchForm: true } : {}), ...(oldStates.isDetailMode ? { isDetailMode: true } : {}) }
                            : null)
                }),
                pageSettingsMeta
            );
    }

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const getCachingConditions = () => {
        const saved = readGridSettings(pageSettingsKey);
        return saved.searchForm && saved.searchForm.conditions && typeof saved.searchForm.conditions === 'object' ? { ...saved.searchForm.conditions } : {};
    };

    const getCachingStates = () => {
        const view = readGridSettings(pageSettingsKey).view || {};
        return { showSearchForm: Boolean(view.showSearchForm), isDetailMode: Boolean(view.isDetailMode) };
    };

    const saveCachingConditions = () => {
        let obj = JSON.parse(stringify(formSearchConditions || {}));
        if (props.search && props.search.default)
            Object.keys(props.search.default).forEach((k) => {
                delete obj[k];
            });

        Object.keys(obj).forEach((k) => {
            if (obj[k] === undefined || obj[k] === null || obj[k] === '') delete obj[k];
        });

        updateGridSettings(pageSettingsKey, (entry) => ({ ...entry, searchForm: Object.keys(obj).length ? { conditions: obj } : null }), pageSettingsMeta);
    };

    const saveCachingStates = () => {
        updateGridSettings(
            pageSettingsKey,
            (entry) => {
                const view = { ...(entry.view || {}) };
                delete view.showSearchForm;
                delete view.isDetailMode;
                if (showSearchForm) view.showSearchForm = true;
                if (isDetailMode) view.isDetailMode = true;
                return { ...entry, view: Object.keys(view).length ? view : null };
            },
            pageSettingsMeta
        );
    };

    const initMenuItem = (mItem) => {
        if (!mItem || !mItem.isMenu) return;

        var getItem = (obj) => {
            if (!obj) return null;
            var current = {
                valueExpr: obj.valueExpr,
                displayExpr: obj.displayExpr,
                icon: obj.icon,
                text: obj.label
                    ? AntLocalization.translateText(AntCondition.getValue({}, obj.label))
                    : AntLocalization.translateText(AntCondition.getValue({}, obj.text)),

                value: obj.name,
                name: obj.name,
                width: obj.width,
                stylingMode: obj.stylingMode ? obj.stylingMode : 'text'
            };

            if (obj.items instanceof Array && obj.items.length > 0) {
                current.items = [];

                obj.items.forEach((i) => {
                    var child = getItem(i);
                    if (child) current.items.push(child);
                });
            }
            return current;
        };

        var m = getItem(mItem);
        if (m) {
            return {
                ...mItem,
                widget: 'Menu',
                location: mItem.location,
                items: [m]
            };
        }
    };

    const initGridSearchGroups = (grid) => {
        var actions = [];

        if (grid) {
            if (grid.showFilterGroups) {
                actions.push({
                    name: 'filterGroups',
                    widget: 'ButtonGroup',
                    location: 'before',
                    groupPack: grid.filterGroupPack ? grid.filterGroupPack : undefined
                });
            }

            if (grid.actionItems instanceof Array)
                grid.actionItems.forEach((item) => {
                    if (item) {
                        if (item.isMenu) {
                            var mItem = initMenuItem(item);
                            actions.push(mItem);
                        } else actions.push(item);
                    }
                });

            actions = actions.filter((item) => {
                return AntCondition.isVisible(grid.searchConditions, item);
            });
            actions.forEach((item) => {
                if (item.widget === 'ButtonGroup' && (item.items instanceof Array === false || item.items.length <= 0)) {
                    item.items = config.getCnfSearchGroups(
                        item.groupPack
                            ? item.groupPack
                            : grid.table
                            ? grid.table
                            : grid.API
                            ? grid.API.entityName
                            : props.API
                            ? props.API.entityName
                            : null
                    );

                    if (item.items instanceof Array) {
                        let found = item.items.find((i) => Boolean(i.default));
                        if (found) item.defaultSelect = found.name;
                    }
                    if (item.items instanceof Array) {
                        item.items = item.items.filter((child) => {
                            return AntCondition.isVisible(grid.searchConditions, child);
                        });

                        if (item.defaultSelect) {
                            if (!item.items.some((i) => i.name === item.defaultSelect)) {
                                item.defaultSelect = item.items.length > 0 ? item.items[0].name : undefined;
                            }
                        }
                    }
                }

                item.editable = AntCondition.isEditable(grid.searchConditions, item);
            });
        }

        return actions;
    };

    const initToolbarActions = (toolbar) => {
        var actions = [];

        if (toolbar) {
            if (toolbar.items instanceof Array)
                toolbar.items.forEach((item) => {
                    if (item) {
                        if (item.isMenu) {
                            var mItem = initMenuItem(item);
                            actions.push(mItem);
                        } else {
                            if (item.name === 'actionCreate' && !item.text && !item.label)
                                item = { ...item, text: { vi: 'Tạo mới', en: 'New', fr: 'Nouveau', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '새로 만들기', hi: 'नया', ar: 'جديد' } };
                            actions.push(item);
                        }
                    }
                });
        }

        if (opts.showBatchExecute) {
            actions.push({
                icon: 'video',
                name: 'runBatchExecute',
                hint: AntLocalization.translateText({ vi: 'Chạy nghiệp vụ hàng loạt', en: 'Run batch actions', fr: 'Lancer les actions par lot', es: 'Ejecutar acciones en lote', id: 'Jalankan aksi massal', ja: '一括処理を実行', zh: '批量执行操作', ko: '일괄 작업 실행', hi: 'बैच कार्रवाई चलाएँ', ar: 'تنفيذ إجراءات مجمّعة' })
            });
        }

        if (props.grid) {
            actions = actions.filter((item) => {
                return AntCondition.isVisible(props.grid.searchConditions, item);
            });
            actions.forEach((act) => {
                act.editable = AntCondition.isEditable(props.grid.searchConditions, act);

                if (act.table && act.module) {
                    act.API = new AntAPI(act.module, act.table);
                }
            });
        }

        if (props.pivotGrid) {
            actions = actions.filter((item) => {
                return AntCondition.isVisible(props.pivotGrid.searchConditions, item);
            });
            actions.forEach((act) => {
                act.editable = AntCondition.isEditable(props.pivotGrid.searchConditions, act);
            });
        }

        return actions;
    };

    const initSearchFields = (grid) => {
        var ar = [];

        if (grid && grid.searchFields instanceof Array)
            grid.searchFields.forEach((f) => {
                var found = ar.find((sF) => sF === f || (typeof sF === 'object' && sF && sF.field === f));
                if (!found) ar.push(f);
            });
        if (grid && typeof grid.searchConditions === 'object' && grid.searchConditions !== null) {
            Object.keys(grid.searchConditions).map((f) => {
                var found = ar.find((sF) => sF === f || (typeof sF === 'object' && sF && sF.field === f));
                if (!found) ar.push(f);
            });
        }
        if (props.search && props.search.items instanceof Array) {
            props.search.items.forEach((item) => {
                if (item.dataField) {
                    var found = ar.find((sF) => sF === item.dataField || (typeof sF === 'object' && sF && sF.field === item.dataField));
                    if (!found) ar.push(item.dataField);
                }
            });
        }
        return ar;
    };

    // msgBox là ref: hộp thông báo nằm ở msgBox.current (trước đây gọi msgBox.show → lỗi khi BE trả lỗi lúc Thêm).
    const showAlert = (args, onClosedCallback) => {
        if (msgBox && msgBox.current) msgBox.current.show(args, onClosedCallback);
    };

    const onQuickCreateClick = useCallback(async (btn) => {
        var result = btn && btn.API ? await btn.API.postQuickCreate(btn.postData) : await API.postQuickCreate(btn.postData);
        if (result && result.error) {
            showAlert({
                content: result.error
            });
        } else {
            if (result.data) {
                // Ghép đường dẫn dùng chung với menu Thêm nhanh trên header (AntQuickCreate).
                if (btn && btn.options && (btn.options.navigateLink || btn.options.navigateLinkBySys)) {
                    var link = resolveNavigateLink(btn.options, result.data, { busSettings: AntAuth.getSessionInfo('bus_settings') || {} });
                    if (link) navigate(link);
                }
            }
        }
    }, [navigate]);

    const onBatchExecuted = useCallback(() => {
        if (grid.current) grid.current.refresh();
    }, []);

    const initOpts = () => {
        var showBatchExecute = false;
        if (
            typeof props.batchExecute === 'object' &&
            props.batchExecute !== null &&
            props.batchExecute.actions instanceof Array &&
            props.batchExecute.actions.length > 0
        )
            showBatchExecute = AntCondition.isVisible(null, props.batchExecute);

        return {
            showFilter:
                canRender &&
                (props.search && props.search.showFilter !== undefined
                    ? props.search.showFilter
                    : props.search && props.search.items instanceof Array && props.search.items.length > 0
                    ? true
                    : false),

            showDetail: canRender && (props.detailGrid || props.detailPivotGrid),
            entityName: props.API && props.API.entityName ? props.API.entityName : '',
            entityDisplay: props.entityDisplay
                ? props.entityDisplay
                : props.useDetailEntityDisplay && props.detailGrid && props.detailGrid.API && props.detailGrid.API.entityDisplay
                ? props.detailGrid.API.entityDisplay
                : props.API && props.API.entityDisplay
                ? props.API.entityDisplay
                : '',
            showBatchExecute: showBatchExecute,
            showImport: props.import && AntCondition.isVisible(null, props.import),
            showGridImport: props.grid && props.grid.import && AntCondition.isVisible(null, props.grid.import),
            showDetailGridImport: props.detailGrid && props.detailGrid.import && AntCondition.isVisible(null, props.detailGrid.import)
        };
    };

    const defaultConds = useMemo(() => getCachingConditions(), []);
    const defaultStates = useMemo(() => getCachingStates(), []);
    const opts = useMemo(() => initOpts(), [defaultStates]);
    const toolbarActions = useMemo(() => initToolbarActions(props.toolbar), [opts]);

    const onAction = useCallback(async (e) => {
        var btn = toolbarActions.find((a) => a.name === e.name);

        if (e.name === 'runBatchExecute' && batchExecutePopup.current && batchExecutePopup.current.show) batchExecutePopup.current.show();
        else if (btn && btn.name === 'actionCreate' && btn.options && btn.options.isQuickCreate) await onQuickCreateClick(btn);
        else if (btn?.waiting) {
            if (props.onAction) {
                await props.onAction(btn);
            }

            // autoReLoad();
        } else if (props.onAction) await props.onAction(btn);
    }, [toolbarActions, onQuickCreateClick, props.onAction]);

    const grid = useRef();
    const detailGrid = useRef();
    const pivotGrid = useRef();
    const detailPivotGrid = useRef();
    const toolbar = useRef();
    const msgBox = useRef();
    const batchExecutePopup = useRef();
    const importPopup = useRef();
    const importGridPopup = useRef();
    const importDetailGridPopup = useRef();

    const [mainState, setMainState] = useState({
        toolbarProcessingButton: null
    });
    const [showSearchForm, setShowSearchForm] = useState(
        Object.keys(defaultConds).length > 0 || (defaultStates && Boolean(defaultStates.showSearchForm))
    );

    const [isDetailMode, setIsDetailMode] = useState(
        defaultStates && defaultStates.isDetailMode !== undefined
            ? Boolean(defaultStates.isDetailMode)
            : props.detailModeAsDefault !== undefined
            ? Boolean(props.detailModeAsDefault)
            : false
    );

    const [formSearchConditions, setFormSearchConditions] = useState({ ...defaultConds });
    const mainSearchConditions = useMemo(() => {
        return {
            ...formSearchConditions,
            ...(props.grid && props.grid.searchConditions ? props.grid.searchConditions : {}),
            ...(props.pivotGrid && props.pivotGrid.searchConditions ? props.pivotGrid.searchConditions : {})
        };
    }, [formSearchConditions]);

    const detailSearchConditions = useMemo(() => {
        return {
            ...formSearchConditions,
            ...(props.detailGrid && props.detailGrid.searchConditions ? props.detailGrid.searchConditions : {}),
            ...(props.detailPivotGrid && props.detailPivotGrid.searchConditions ? props.detailPivotGrid.searchConditions : {})
        };
    }, [formSearchConditions]);

    const loadData = useCallback(() => {
        if (isDetailMode) {
            if (detailGrid.current) detailGrid.current.reload();
            if (detailPivotGrid.current) detailPivotGrid.current.reload();
        } else {
            if (grid.current) grid.current.reload();
        }
    }, [isDetailMode]);

    useEffect(() => {
        AntAuth.checkValidSession();
    }, []);

    useEffect(() => {
        saveCachingConditions();
    }, [formSearchConditions]);

    useEffect(() => {
        saveCachingStates();
    }, [isDetailMode, showSearchForm]);

    // useEffect(() => {
    //     loadData();
    // }, [isDetailMode]);

    const gridFilterGroups = useMemo(() => initGridSearchGroups(props.pivotGrid ? props.pivotGrid : props.grid), []);

    const detailGridFilterGroups = useMemo(() => initGridSearchGroups(props.detailPivotGrid ? props.detailPivotGrid : props.detailGrid), []);

    const searchFields = useMemo(() => initSearchFields(props.pivotGrid ? props.pivotGrid : props.grid), []);
    const detailSearchFields = useMemo(() => initSearchFields(props.detailPivotGrid ? props.detailPivotGrid : props.detailGrid), []);

    const displayDetail = useRef(Boolean(opts.showDetail) && Boolean(isDetailMode));
    displayDetail.current = Boolean(opts.showDetail) && Boolean(isDetailMode);

    const showPopup = useCallback((ref, defaultObj) => {
        if (!ref) return;

        var obj = {};
        if (defaultObj && typeof defaultObj === 'object') obj = { ...obj, ...defaultObj };

        if (ref.show) ref.show(obj);
        else if (ref.current && ref.current.show) ref.current.show(obj);
        return;
    }, []);

    useImperativeHandle(ref, () => ({
        showPopup: showPopup,
        loadData: loadData,
        getSearchConditions: () => mainSearchConditions
    }));

    const [shouldRender, setShouldRender] = useState(1);
    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);
    const forceRenderWithState = useCallback((data) => {
        setMainState((v) => Object.assign(v, data));
        setShouldRender((v) => v + 1);
    }, []);

    const isRender = shouldRender + (props.shouldRender ? props.shouldRender : 1);

    const isAllowGridQuery = useCallback(() => {
        return !Boolean(displayDetail.current);
    }, []);

    const isAllowDetailGridQuery = useCallback(() => {
        return Boolean(displayDetail.current);
    }, []);

    const onImport = useCallback(() => {
        if (importPopup.current) importPopup.current.show();
    }, []);
    const onImportGrid = useCallback(() => {
        if (importPopup.current) importPopup.current.show();
        if (importGridPopup.current) importGridPopup.current.show();
    }, []);
    const onImportDetailGrid = useCallback(() => {
        if (importDetailGridPopup.current) importDetailGridPopup.current.show();
    }, []);

    const onFilterClick = useCallback(() => setShowSearchForm((v) => !Boolean(v)), []);

    const onDetailClick = useCallback(() => {
        setIsDetailMode((v) => !Boolean(v));
        loadData();
    }, [loadData]);

    const onActionProcessing = useCallback((btn) => {
        forceRenderWithState({ toolbarProcessingButton: btn.name });
    }, [forceRenderWithState]);

    const onActionProceeed = useCallback(() => {
        forceRenderWithState({ toolbarProcessingButton: null });
    }, [forceRenderWithState]);

    const emptyOnImported = useCallback(() => {}, []);

    return (
        <React.Fragment>
            {isRender && (
                <AntPage
                    className={'ant-search-page'}
                    marginLeft={5}
                    marginRight={5}
                    backgroundImage={appGlobal.appAssets?.images?.antSearchPageBackground}
                    hideBackgroundImage={!canRender}
                >
                    <AntToolbar
                        ref={toolbar}
                        API={props.API}
                        shouldRender={isRender}
                        hideTitle={props.hideTitle}
                        title={props.title ? props.title : opts.entityDisplay}
                        titleIcon={props.titleIcon}
                        processingButton={mainState.toolbarProcessingButton}
                        table={props.table}
                        showHelp={canRender && (props.toolbar && props.toolbar.showHelp !== undefined ? props.toolbar.showHelp : undefined)}
                        showFilter={props.search && props.search.alwaysVisible ? false : opts.showFilter}
                        showDetail={opts.showDetail}
                        isDetailMode={isDetailMode}
                        onFilterClick={onFilterClick}
                        onDetailClick={onDetailClick}
                        items={canRender ? toolbarActions : []}
                        onAction={onAction}
                        onActionProcessing={onActionProcessing}
                        onActionProceeed={onActionProceeed}
                    />

                    {!canRender && (
                        <AntErrorPage
                            id={`ant-search-error-page-${props.API.entityName}`}
                            text={AntLocalization.translateText({ vi: 'Không có quyền truy cập !', en: 'Access Denied', fr: 'Accès refusé', es: 'Acceso denegado', id: 'Akses ditolak', ja: 'アクセス拒否', zh: '访问被拒绝', ko: '접근 거부', hi: 'पहुँच अस्वीकृत', ar: 'تم رفض الوصول' })}
                        />
                    )}

                    {canRender && (
                        <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                            {props.leftSidebar && props.leftSidebar.enabled && (
                                <AntLeftSidebar
                                    variant={props.leftSidebar.variant || 'search'}
                                    data={{
                                        filterGroups: props.grid && props.grid.filterGroups,
                                        entityName: props.API ? props.API.entityName : undefined
                                    }}
                                    panels={props.leftSidebar.panels}
                                    mockMode={props.leftSidebar.mockMode !== false}
                                />
                            )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <AntMessageBox ref={msgBox} />

                            <div style={{ display: (props.search && props.search.alwaysVisible) || showSearchForm ? 'block' : 'none' }}>
                                <p />
                                <AntSearchForm
                                    formData={formSearchConditions}
                                    prepareConditions={props.search ? props.search.prepareConditions : undefined}
                                    default={props.search ? props.search.default : undefined}
                                    API={props.search && props.search.API ? props.search.API : props.API}
                                    table={props.search && props.search.table ? props.search.table : props.table}
                                    entityName={props.API.entityName ? props.API.entityName : props.entityName}
                                    entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                                    colCount={props.search && props.search.colCount !== undefined ? props.search.colCount : undefined}
                                    colCountDefault={props.search ? props.search.colCountDefault : undefined}
                                    // onConditionsChanged={setFormSearchConditions}
                                    onFormDataChanged={setFormSearchConditions}
                                    fields={props.fields}
                                    items={props.search && props.search.items instanceof Array ? props.search.items : []}
                                />
                                <p />
                            </div>

                            {typeof props.renderHeader === 'function' && props.renderHeader()}

                            <div style={{ display: !displayDetail.current ? 'block' : 'none' }}>
                                {props.grid && (
                                    <AntSearchGrid
                                        ref={grid}
                                        API={props.API}
                                        table={props.grid && props.grid.table ? props.grid.table : props.table}
                                        url={props.grid ? props.grid.url : undefined}
                                        entityName={props.API.entityName ? props.API.entityName : props.entityName}
                                        entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                                        sortable={props.grid ? props.grid.sortable : true}
                                        focusable={props.grid ? props.grid.focusable : undefined}
                                        focusedRowEnabled={props.grid ? props.grid.focusedRowEnabled : undefined}
                                        selectable={props.grid ? props.grid.selectable : undefined}
                                        selectionMode={props.grid ? props.grid.selectionMode : undefined}
                                        loadFunc={props.grid ? props.grid.loadFunc : undefined}
                                        isAllowQuery={isAllowGridQuery}
                                        categoriesQueryEnabled={props.grid?.deferUntilVisible ? !isDetailMode : undefined}
                                        searchConditions={mainSearchConditions}
                                        searchFields={searchFields.length > 0 ? searchFields : undefined}
                                        remotePaging={props.grid ? props.grid.remotePaging : undefined}
                                        updateMode={props.grid ? props.grid.updateMode : undefined}
                                        allowedPageSizes={
                                            props.grid && props.grid.allowedPageSizes !== undefined ? props.grid.allowedPageSizes : DEFAULT_PAGE_SIZES
                                        }
                                        defaultPageSize={props.grid && props.grid.defaultPageSize ? props.grid.defaultPageSize : undefined}
                                        orderBy={props.grid && props.grid.orderBy ? props.grid.orderBy : undefined}
                                        orderType={props.grid && props.grid.orderType ? props.grid.orderType : undefined}
                                        cellStyles={props.grid && props.grid.cellStyles instanceof Array ? props.grid.cellStyles : undefined}
                                        cellRenders={props.grid ? props.grid.cellRenders : undefined}
                                        cellContext={props.grid ? props.grid.cellContext : undefined}
                                        rowStyles={props.grid && props.grid.rowStyles instanceof Array ? props.grid.rowStyles : undefined}
                                        showRowActions={props.grid ? props.grid.showRowActions : undefined}
                                        rowActionPack={props.grid ? props.grid.rowActionPack : undefined}
                                        rowActions={props.grid && props.grid.rowActions instanceof Array ? props.grid.rowActions : undefined}
                                        excludesRowActions={props.grid ? props.grid.excludesRowActions : undefined}
                                        rowActionWidth={props.grid ? props.grid.rowActionWidth : undefined}
                                        rowActionsDisplay={props.grid ? props.grid.rowActionsDisplay : undefined}
                                        rowActionPrimaryActions={props.grid ? props.grid.rowActionPrimaryActions : undefined}
                                        batchActions={props.grid ? props.grid.batchActions : undefined}
                                        autoBatchActions={props.grid ? props.grid.autoBatchActions : undefined}
                                        canEditAction={props.grid ? props.grid.canEditAction : undefined}
                                        canDeleteAction={props.grid ? props.grid.canDeleteAction : undefined}
                                        onBatchExecuted={props.grid ? props.grid.onBatchExecuted : undefined}
                                        rowAlternationEnabled={props.grid ? props.grid.rowAlternationEnabled : undefined}
                                        hoverStateEnabled={props.grid ? props.grid.hoverStateEnabled : undefined}
                                        showDownload={props.grid && props.grid.showDownload !== undefined ? props.grid.showDownload : undefined}
                                        showActionBar={props.grid && props.grid.showActionBar !== undefined ? props.grid.showActionBar : true}
                                        showSearchBar={props.grid && props.grid.showSearchBar !== undefined ? props.grid.showSearchBar : true}
                                        showSearchCategories={
                                            props.grid && props.grid.showSearchCategories !== undefined ? props.grid.showSearchCategories : undefined
                                        }
                                        showPageSizeSelector={
                                            props.grid && props.grid.showPageSizeSelector !== undefined ? props.grid.showPageSizeSelector : undefined
                                        }
                                        // autoFocusSearchBar={!showSearchForm}
                                        autoFocusSearchBar={false}
                                        showFieldChooser={
                                            props.grid && props.grid.showFieldChooser !== undefined ? props.grid.showFieldChooser : undefined
                                        }
                                        showIndex={props.grid && props.grid.showIndex !== undefined ? props.grid.showIndex : undefined}
                                        showDetail={props.grid && props.grid.showDetail !== undefined ? props.grid.showDetail : undefined}
                                        showNote={props.grid && props.grid.showNote !== undefined ? props.grid.showNote : undefined}
                                        showAttachments={
                                            props.grid && props.grid.showAttachments !== undefined ? props.grid.showAttachments : undefined
                                        }
                                        showPriority={props.grid && props.grid.showPriority !== undefined ? props.grid.showPriority : undefined}
                                        priorityField={props.grid && props.grid.priorityField !== undefined ? props.grid.priorityField : undefined}
                                        showImport={opts.showImport || opts.showGridImport}
                                        showAdd={opts.showAdd}
                                        add={props.add}
                                        import={props.grid && props.grid.import !== undefined ? props.grid.import : props.import}
                                        onImport={onImportGrid}
                                        fields={props.fields}
                                        queryFields={props.grid.queryFields}
                                        selectFields={props.grid.selectFields}
                                        actionItems={gridFilterGroups}
                                        onRowAction={props.grid ? props.grid.onRowAction : undefined}
                                        scheduler={props.grid ? props.grid.scheduler : undefined}
                                        cardview={props.grid ? props.grid.cardview : undefined}
                                        defaultView={props.grid ? props.grid.defaultView : undefined}
                                        columns={props.grid && props.grid.columns instanceof Array ? props.grid.columns : []}
                                        searchCategories1={props.grid && props.grid.searchCategories1 ? props.grid.searchCategories1 : undefined}
                                        searchCategories2={props.grid && props.grid.searchCategories2 ? props.grid.searchCategories2 : undefined}
                                        searchCategories3={props.grid && props.grid.searchCategories3 ? props.grid.searchCategories3 : undefined}
                                        searchCategories4={props.grid && props.grid.searchCategories4 ? props.grid.searchCategories4 : undefined}
                                        searchCategories5={props.grid && props.grid.searchCategories5 ? props.grid.searchCategories5 : undefined}
                                        searchCategories6={props.grid && props.grid.searchCategories6 ? props.grid.searchCategories6 : undefined}
                                        searchCategories7={props.grid && props.grid.searchCategories7 ? props.grid.searchCategories7 : undefined}
                                        searchCategories8={props.grid && props.grid.searchCategories8 ? props.grid.searchCategories8 : undefined}
                                        searchCategories9={props.grid && props.grid.searchCategories9 ? props.grid.searchCategories9 : undefined}
                                        searchCategories10={props.grid && props.grid.searchCategories10 ? props.grid.searchCategories10 : undefined}
                                        summaryHeader={props.grid && props.grid.summaryHeader ? props.grid.summaryHeader : undefined}
                                        dateFilterField={props.grid ? props.grid.dateFilterField : undefined}
                                        settingsKey={props.grid ? props.grid.settingsKey : undefined}
                                        showDateFilter={props.grid ? props.grid.showDateFilter : undefined}
                                    />
                                )}
                                {props.pivotGrid && (
                                    <AntSearchPivotGrid
                                        ref={pivotGrid}
                                        chart={props.pivotGrid.chart ? props.pivotGrid.chart : props.chart}
                                        showChart={props.pivotGrid.showChart}
                                        API={props.API}
                                        table={props.pivotGrid && props.pivotGrid.table ? props.pivotGrid.table : props.table}
                                        url={props.pivotGrid ? props.pivotGrid.url : undefined}
                                        entityName={props.API.entityName ? props.API.entityName : props.entityName}
                                        entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                                        loadFunc={props.pivotGrid ? props.pivotGrid.loadFunc : undefined}
                                        isAllowQuery={() => !Boolean(displayDetail.current)}
                                        searchConditions={mainSearchConditions}
                                        searchFields={searchFields.length > 0 ? searchFields : undefined}
                                        showDownload={props.pivotGrid.showDownload}
                                        showActionBar={
                                            props.pivotGrid && props.pivotGrid.showActionBar !== undefined ? props.pivotGrid.showActionBar : true
                                        }
                                        showSearchBar={
                                            props.pivotGrid && props.pivotGrid.showSearchBar !== undefined ? props.pivotGrid.showSearchBar : true
                                        }
                                        showPageSizeSelector={
                                            props.pivotGrid && props.pivotGrid.showPageSizeSelector !== undefined
                                                ? props.pivotGrid.showPageSizeSelector
                                                : undefined
                                        }
                                        // autoFocusSearchBar={!showSearchForm}
                                        autoFocusSearchBar={false}
                                        fields={props.pivotGrid ? props.pivotGrid.fields : undefined}
                                        actionItems={gridFilterGroups}
                                        onRowAction={props.pivotGrid ? props.pivotGrid.onRowAction : undefined}
                                        scheduler={props.pivotGrid ? props.pivotGrid.scheduler : undefined}
                                        cardview={props.pivotGrid ? props.pivotGrid.cardview : undefined}
                                        configFields={(props.pivotGrid.configFields instanceof Array ? props.pivotGrid.configFields : []).filter(
                                            (f) => f.displayMode === 'summary' || f.displayMode === undefined
                                        )}
                                        queryFields={props.pivotGrid.queryFields}
                                        selectFields={props.pivotGrid.selectFields}
                                        options={props.pivotGrid ? props.pivotGrid.options : undefined}
                                        onCellPrepared={props.pivotGrid ? props.pivotGrid.onCellPrepared : undefined}
                                        showColumnTotals={props.pivotGrid ? props.pivotGrid.showColumnTotals : undefined}
                                        showColumnGrandTotals={props.pivotGrid ? props.pivotGrid.showColumnGrandTotals : undefined}
                                        showRowTotals={props.pivotGrid ? props.pivotGrid.showRowTotals : undefined}
                                        showRowGrandTotals={props.pivotGrid ? props.pivotGrid.showRowGrandTotals : undefined}
                                        isMonthlyReport={props.pivotGrid.isMonthlyReport}
                                        isCurrency={props.pivotGrid.isCurrency}
                                        defaultPageSize={
                                            props.pivotGrid && props.pivotGrid.defaultPageSize ? props.pivotGrid.defaultPageSize : undefined
                                        }
                                        searchCategories1={
                                            props.pivotGrid && props.pivotGrid.searchCategories1 ? props.pivotGrid.searchCategories1 : undefined
                                        }
                                        searchCategories2={
                                            props.pivotGrid && props.pivotGrid.searchCategories2 ? props.pivotGrid.searchCategories2 : undefined
                                        }
                                        searchCategories3={
                                            props.pivotGrid && props.pivotGrid.searchCategories3 ? props.pivotGrid.searchCategories3 : undefined
                                        }
                                        searchCategories4={
                                            props.pivotGrid && props.pivotGrid.searchCategories4 ? props.pivotGrid.searchCategories4 : undefined
                                        }
                                        searchCategories5={
                                            props.pivotGrid && props.pivotGrid.searchCategories5 ? props.pivotGrid.searchCategories5 : undefined
                                        }
                                        searchCategories6={
                                            props.pivotGrid && props.pivotGrid.searchCategories6 ? props.pivotGrid.searchCategories6 : undefined
                                        }
                                        searchCategories7={
                                            props.pivotGrid && props.pivotGrid.searchCategories7 ? props.pivotGrid.searchCategories7 : undefined
                                        }
                                        searchCategories8={
                                            props.pivotGrid && props.pivotGrid.searchCategories8 ? props.pivotGrid.searchCategories8 : undefined
                                        }
                                        searchCategories9={
                                            props.pivotGrid && props.pivotGrid.searchCategories9 ? props.pivotGrid.searchCategories9 : undefined
                                        }
                                        searchCategories10={
                                            props.pivotGrid && props.pivotGrid.searchCategories10 ? props.pivotGrid.searchCategories10 : undefined
                                        }
                                    />
                                )}
                            </div>

                            {opts.showDetail && (
                                <div style={{ display: displayDetail.current ? 'block' : 'none' }}>
                                    {props.detailGrid && (
                                        <AntSearchGrid
                                            ref={detailGrid}
                                            API={props.detailGrid && props.detailGrid.API ? props.detailGrid.API : props.API}
                                            table={props.detailGrid && props.detailGrid.table ? props.detailGrid.table : props.table}
                                            url={props.detailGrid ? props.detailGrid.url : undefined}
                                            entityName={props.detailGrid.API.entityName ? props.detailGrid.API.entityName : props.entityName}
                                            entityDisplay={
                                                props.detailGrid.API.entityDisplay ? props.detailGrid.API.entityDisplay : props.entityDisplay
                                            }
                                            sortable={true}
                                            focusable={props.detailGrid ? props.detailGrid.focusable : undefined}
                                            selectable={props.detailGrid ? props.detailGrid.selectable : undefined}
                                            selectionMode={props.detailGrid ? props.detailGrid.selectionMode : undefined}
                                            loadFunc={props.detailGrid ? props.detailGrid.loadFunc : undefined}
                                            isAllowQuery={isAllowDetailGridQuery}
                                            categoriesQueryEnabled={props.detailGrid.deferUntilVisible ? isDetailMode : undefined}
                                            searchConditions={detailSearchConditions}
                                            searchFields={detailSearchFields.length > 0 ? detailSearchFields : undefined}
                                            remotePaging={props.detailGrid ? props.detailGrid.remotePaging : undefined}
                                            updateMode={props.detailGrid ? props.detailGrid.updateMode : undefined}
                                            allowedPageSizes={
                                                props.detailGrid && props.detailGrid.allowedPageSizes !== undefined
                                                    ? props.detailGrid.allowedPageSizes
                                                    : DEFAULT_DETAIL_PAGE_SIZES
                                            }
                                            defaultPageSize={
                                                props.detailGrid && props.detailGrid.defaultPageSize ? props.detailGrid.defaultPageSize : undefined
                                            }
                                            cellStyles={
                                                props.detailGrid && props.detailGrid.cellStyles instanceof Array
                                                    ? props.detailGrid.cellStyles
                                                    : undefined
                                            }
                                            cellRenders={props.detailGrid ? props.detailGrid.cellRenders : undefined}
                                            cellContext={props.detailGrid ? props.detailGrid.cellContext : undefined}
                                            rowStyles={
                                                props.detailGrid && props.detailGrid.rowStyles instanceof Array
                                                    ? props.detailGrid.rowStyles
                                                    : undefined
                                            }
                                            showRowActions={props.detailGrid ? props.detailGrid.showRowActions : undefined}
                                            rowActionPack={props.detailGrid ? props.detailGrid.rowActionPack : undefined}
                                            excludesRowActions={props.detailGrid ? props.detailGrid.excludesRowActions : undefined}
                                            rowActionWidth={props.detailGrid ? props.detailGrid.rowActionWidth : undefined}
                                            rowActionsDisplay={props.detailGrid ? props.detailGrid.rowActionsDisplay : undefined}
                                            rowActionPrimaryActions={props.detailGrid ? props.detailGrid.rowActionPrimaryActions : undefined}
                                            batchActions={props.detailGrid ? props.detailGrid.batchActions : undefined}
                                            autoBatchActions={props.detailGrid ? props.detailGrid.autoBatchActions : undefined}
                                            canEditAction={props.detailGrid ? props.detailGrid.canEditAction : undefined}
                                            canDeleteAction={props.detailGrid ? props.detailGrid.canDeleteAction : undefined}
                                            onBatchExecuted={props.detailGrid ? props.detailGrid.onBatchExecuted : undefined}
                                            rowActions={
                                                props.detailGrid && props.detailGrid.rowActions instanceof Array
                                                    ? props.detailGrid.rowActions
                                                    : undefined
                                            }
                                            showDownload={
                                                props.detailGrid && props.detailGrid.showDownload !== undefined
                                                    ? props.detailGrid.showDownload
                                                    : undefined
                                            }
                                            showActionBar={
                                                props.detailGrid && props.detailGrid.showActionBar !== undefined
                                                    ? props.detailGrid.showActionBar
                                                    : true
                                            }
                                            showSearchBar={
                                                props.detailGrid && props.detailGrid.showSearchBar !== undefined
                                                    ? props.detailGrid.showSearchBar
                                                    : true
                                            }
                                            showPageSizeSelector={
                                                props.detailGrid && props.detailGrid.showPageSizeSelector !== undefined
                                                    ? props.detailGrid.showPageSizeSelector
                                                    : undefined
                                            }
                                            // autoFocusSearchBar={!showSearchForm}
                                            autoFocusSearchBar={false}
                                            showFieldChooser={
                                                props.detailGrid && props.detailGrid.showFieldChooser !== undefined
                                                    ? props.detailGrid.showFieldChooser
                                                    : undefined
                                            }
                                            showIndex={
                                                props.detailGrid && props.detailGrid.showIndex !== undefined ? props.detailGrid.showIndex : undefined
                                            }
                                            showDetail={
                                                props.detailGrid && props.detailGrid.showDetail !== undefined
                                                    ? props.detailGrid.showDetail
                                                    : undefined
                                            }
                                            showNote={
                                                props.detailGrid && props.detailGrid.showNote !== undefined ? props.detailGrid.showNote : undefined
                                            }
                                            showAttachments={
                                                props.detailGrid && props.detailGrid.showAttachments !== undefined
                                                    ? props.detailGrid.showAttachments
                                                    : undefined
                                            }
                                            showPriority={
                                                props.detailGrid && props.detailGrid.showPriority !== undefined
                                                    ? props.detailGrid.showPriority
                                                    : undefined
                                            }
                                            priorityField={
                                                props.detailGrid && props.detailGrid.priorityField !== undefined
                                                    ? props.detailGrid.priorityField
                                                    : undefined
                                            }
                                            showImport={opts.showDetailGridImport}
                                            import={
                                                props.detailGrid && props.detailGrid.import !== undefined ? props.detailGrid.import : props.import
                                            }
                                            onImport={onImportDetailGrid}
                                            fields={props.detailGrid ? props.detailGrid.fields : undefined}
                                            queryFields={props.detailGrid.queryFields}
                                            selectFields={props.detailGrid.selectFields}
                                            actionItems={detailGridFilterGroups}
                                            onRowAction={props.detailGrid ? props.detailGrid.onRowAction : undefined}
                                            scheduler={props.detailGrid ? props.detailGrid.scheduler : undefined}
                                            cardview={props.detailGrid ? props.detailGrid.cardview : undefined}
                                            columns={props.detailGrid && props.detailGrid.columns instanceof Array ? props.detailGrid.columns : []}
                                            searchCategories1={
                                                props.detailGrid && props.detailGrid.searchCategories1
                                                    ? props.detailGrid.searchCategories1
                                                    : undefined
                                            }
                                            searchCategories2={
                                                props.detailGrid && props.detailGrid.searchCategories2
                                                    ? props.detailGrid.searchCategories2
                                                    : undefined
                                            }
                                            searchCategories3={
                                                props.detailGrid && props.detailGrid.searchCategories3
                                                    ? props.detailGrid.searchCategories3
                                                    : undefined
                                            }
                                            searchCategories4={
                                                props.detailGrid && props.detailGrid.searchCategories4
                                                    ? props.detailGrid.searchCategories4
                                                    : undefined
                                            }
                                            searchCategories5={
                                                props.detailGrid && props.detailGrid.searchCategories5
                                                    ? props.detailGrid.searchCategories5
                                                    : undefined
                                            }
                                            searchCategories6={
                                                props.detailGrid && props.detailGrid.searchCategories6
                                                    ? props.detailGrid.searchCategories6
                                                    : undefined
                                            }
                                            searchCategories7={
                                                props.detailGrid && props.detailGrid.searchCategories7
                                                    ? props.detailGrid.searchCategories7
                                                    : undefined
                                            }
                                            searchCategories8={
                                                props.detailGrid && props.detailGrid.searchCategories8
                                                    ? props.detailGrid.searchCategories8
                                                    : undefined
                                            }
                                            searchCategories9={
                                                props.detailGrid && props.detailGrid.searchCategories9
                                                    ? props.detailGrid.searchCategories9
                                                    : undefined
                                            }
                                            searchCategories10={
                                                props.detailGrid && props.detailGrid.searchCategories10
                                                    ? props.detailGrid.searchCategories10
                                                    : undefined
                                            }
                                            summaryHeader={
                                                props.detailGrid && props.detailGrid.summaryHeader ? props.detailGrid.summaryHeader : undefined
                                            }
                                            dateFilterField={props.detailGrid ? props.detailGrid.dateFilterField : undefined}
                                            settingsKey={props.detailGrid ? props.detailGrid.settingsKey : undefined}
                                            showDateFilter={props.detailGrid ? props.detailGrid.showDateFilter : undefined}
                                        />
                                    )}

                                    {props.detailPivotGrid && (
                                        <AntSearchPivotGrid
                                            ref={detailPivotGrid}
                                            chart={props.detailPivotGrid.chart}
                                            showChart={props.detailPivotGrid.showChart}
                                            API={props.API}
                                            table={props.detailPivotGrid && props.detailPivotGrid.table ? props.detailPivotGrid.table : props.table}
                                            url={props.detailPivotGrid ? props.detailPivotGrid.url : undefined}
                                            entityName={
                                                props.detailPivotGrid.API.entityName ? props.detailPivotGrid.API.entityName : props.entityName
                                            }
                                            entityDisplay={
                                                props.detailPivotGrid.API.entityDisplay
                                                    ? props.detailPivotGrid.API.entityDisplay
                                                    : props.entityDisplay
                                            }
                                            loadFunc={props.detailPivotGrid ? props.detailPivotGrid.loadFunc : undefined}
                                            isAllowQuery={() => Boolean(displayDetail.current)}
                                            searchConditions={detailSearchConditions}
                                            searchFields={detailSearchFields.length > 0 ? detailSearchFields : undefined}
                                            showDownload={props.detailPivotGrid.showDownload}
                                            showActionBar={props.detailPivotGrid.showActionBar}
                                            showSearchBar={props.detailPivotGrid.showSearchBar}
                                            showPageSizeSelector={props.detailPivotGrid.showPageSizeSelector}
                                            // autoFocusSearchBar={!showSearchForm}
                                            autoFocusSearchBar={false}
                                            fields={props.detailPivotGrid ? props.detailPivotGrid.fields : undefined}
                                            actionItems={detailGridFilterGroups}
                                            onRowAction={props.detailPivotGrid ? props.detailPivotGrid.onRowAction : undefined}
                                            scheduler={props.detailPivotGrid ? props.detailPivotGrid.scheduler : undefined}
                                            cardview={props.detailPivotGrid ? props.detailPivotGrid.cardview : undefined}
                                            configFields={(props.detailPivotGrid.configFields instanceof Array
                                                ? props.detailPivotGrid.configFields
                                                : []
                                            ).filter((f) => f.displayMode === 'detail' || f.displayMode === undefined)}
                                            queryFields={props.detailPivotGrid.queryFields}
                                            selectFields={props.detailPivotGrid.selectFields}
                                            options={props.detailPivotGrid ? props.detailPivotGrid.options : undefined}
                                            onCellPrepared={props.detailPivotGrid ? props.detailPivotGrid.onCellPrepared : undefined}
                                            showColumnTotals={props.detailPivotGrid ? props.detailPivotGrid.showColumnTotals : undefined}
                                            showColumnGrandTotals={props.detailPivotGrid ? props.detailPivotGrid.showColumnGrandTotals : undefined}
                                            showRowTotals={props.detailPivotGrid ? props.detailPivotGrid.showRowTotals : undefined}
                                            showRowGrandTotals={props.detailPivotGrid ? props.detailPivotGrid.showRowGrandTotals : undefined}
                                            isMonthlyReport={props.detailPivotGrid.isMonthlyReport}
                                            isCurrency={props.detailPivotGrid.isCurrency}
                                            defaultPageSize={
                                                props.detailPivotGrid && props.detailPivotGrid.defaultPageSize
                                                    ? props.detailPivotGrid.defaultPageSize
                                                    : undefined
                                            }
                                            searchCategories1={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories1
                                                    ? props.detailPivotGrid.searchCategories1
                                                    : undefined
                                            }
                                            searchCategories2={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories2
                                                    ? props.detailPivotGrid.searchCategories2
                                                    : undefined
                                            }
                                            searchCategories3={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories3
                                                    ? props.detailPivotGrid.searchCategories3
                                                    : undefined
                                            }
                                            searchCategories4={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories4
                                                    ? props.detailPivotGrid.searchCategories4
                                                    : undefined
                                            }
                                            searchCategories5={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories5
                                                    ? props.detailPivotGrid.searchCategories5
                                                    : undefined
                                            }
                                            searchCategories6={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories6
                                                    ? props.detailPivotGrid.searchCategories6
                                                    : undefined
                                            }
                                            searchCategories7={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories7
                                                    ? props.detailPivotGrid.searchCategories7
                                                    : undefined
                                            }
                                            searchCategories8={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories8
                                                    ? props.detailPivotGrid.searchCategories8
                                                    : undefined
                                            }
                                            searchCategories9={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories9
                                                    ? props.detailPivotGrid.searchCategories9
                                                    : undefined
                                            }
                                            searchCategories10={
                                                props.detailPivotGrid && props.detailPivotGrid.searchCategories10
                                                    ? props.detailPivotGrid.searchCategories10
                                                    : undefined
                                            }
                                        />
                                    )}
                                </div>
                            )}

                            {opts.showBatchExecute && (
                                <AntBatchExecutePopup
                                    ref={batchExecutePopup}
                                    API={props.API}
                                    table={props.grid && props.grid.table ? props.grid.table : props.table}
                                    width={props.batchExecute?.width}
                                    height={props.batchExecute?.height}
                                    onSaved={props.batchExecute?.onSaved}
                                    title={
                                        props.batchExecute?.title
                                            ? AntLocalization.translateText(props.batchExecute.title)
                                            : AntLocalization.translateText({ vi: 'Chạy nghiệp vụ hàng loạt', en: 'Run batch actions', fr: 'Lancer les actions par lot', es: 'Ejecutar acciones en lote', id: 'Jalankan aksi massal', ja: '一括処理を実行', zh: '批量执行操作', ko: '일괄 작업 실행', hi: 'बैच कार्रवाई चलाएँ', ar: 'تنفيذ إجراءات مجمّعة' })
                                    }
                                    onExecuted={onBatchExecuted}
                                    search={{
                                        table: props.search && props.search.table ? props.search.table : props.table,
                                        fields: props.fields,
                                        items:
                                            props.batchExecute?.search?.items instanceof Array
                                                ? props.batchExecute.search.items
                                                : props.search?.items instanceof Array
                                                ? props.search.items.filter((item) => Boolean(item.showInAutomation))
                                                : [],
                                        colCount:
                                            props.batchExecute?.search?.colCount !== undefined
                                                ? props.batchExecute.search.colCount
                                                : props.search?.colCount,
                                        default: props.batchExecute?.search?.default || props.search?.default
                                    }}
                                    grid={{
                                        url: props.grid?.url,
                                        name: 'batchItems',
                                        showRefresh: true,
                                        showSearchBar: true,
                                        showFieldChooser: false,
                                        showFilterBySelected: false,
                                        columns:
                                            props.batchExecute?.grid?.columns instanceof Array
                                                ? props.batchExecute.grid.columns
                                                : props.grid?.columns instanceof Array
                                                ? props.grid.columns
                                                : [],
                                        defaultPageSize: 8,
                                        remotePaging:
                                            props.batchExecute?.grid?.remotePaging !== undefined
                                                ? props.batchExecute.grid.remotePaging
                                                : true,
                                        searchConditions:
                                            props.batchExecute?.grid?.searchConditions !== undefined
                                                ? props.batchExecute.grid.searchConditions
                                                : props.grid?.searchConditions,
                                        searchByHeaderFields:
                                            props.batchExecute?.grid?.searchByHeaderFields instanceof Array
                                                ? [...searchFields, ...props.batchExecute.grid.searchByHeaderFields]
                                                : searchFields,
                                        actionItems:
                                            props.batchExecute?.grid?.actionItems instanceof Array
                                                ? props.batchExecute.grid.actionItems
                                                : gridFilterGroups.filter((action) => !action.isMenu && action.widget !== 'ButtonGroup'),
                                        customSelectionValue: props.batchExecute?.grid?.customSelectionValue,
                                        showDetail:
                                            props.batchExecute?.grid?.showDetail !== undefined
                                                ? props.batchExecute.grid.showDetail
                                                : props.grid?.showDetail,
                                        showNote:
                                            props.batchExecute?.grid?.showNote !== undefined
                                                ? props.batchExecute.grid.showNote
                                                : props.grid?.showNote,
                                        showAttachments:
                                            props.batchExecute?.grid?.showAttachments !== undefined
                                                ? props.batchExecute.grid.showAttachments
                                                : props.grid?.showAttachments,
                                        showPriority:
                                            props.batchExecute?.grid?.showPriority !== undefined
                                                ? props.batchExecute.grid.showPriority
                                                : props.grid?.showPriority,
                                        priorityField:
                                            props.batchExecute?.grid?.priorityField !== undefined
                                                ? props.batchExecute.grid.priorityField
                                                : props.grid?.priorityField
                                    }}
                                    actions={props.batchExecute.actions}
                                />
                            )}

                            {opts.showImport && (
                                <AntImport
                                    ref={importPopup}
                                    API={props.API}
                                    table={
                                        props.import && props.import.table
                                            ? props.import.table
                                            : props.grid && props.grid.table
                                            ? props.grid.table
                                            : props.table
                                    }
                                    entityName={props.API.entityName ? props.API.entityName : props.entityName}
                                    entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                                    previewWidth={props.import && props.import.previewWidth !== undefined ? props.import.previewWidth : undefined}
                                    heights={props.import && props.import.heights !== undefined ? props.import.heights : undefined}
                                    createable={props.import && props.import.createable !== undefined ? props.import.createable : true}
                                    updateable={props.import && props.import.updateable !== undefined ? props.import.updateable : true}
                                    defaultPageSize={props.import ? props.import.defaultPageSize : undefined}
                                    fields={props.fields}
                                    mapping={props.import ? props.import.mapping : []}
                                    aiEnabled={props.import && props.import.useAI === true}
                                    aiJobType={props.import && props.import.aiJobType}
                                    aiTargetTable={props.import && props.import.aiTargetTable}
                                    aiVariant={props.import && props.import.aiVariant}
                                    aiEntity={props.import && props.import.aiEntity}
                                    aiExtraInputData={props.import && props.import.aiExtraInputData}
                                    onImported={emptyOnImported}
                                />
                            )}
                            {opts.showGridImport && props.grid && (
                                <AntImport
                                    ref={importGridPopup}
                                    API={props.grid.API}
                                    table={props.grid && props.grid.table ? props.grid.table : props.table}
                                    entityName={props.grid.API.entityName ? props.grid.API.entityName : props.entityName}
                                    entityDisplay={props.grid.API.entityDisplay ? props.grid.API.entityDisplay : props.entityDisplay}
                                    previewWidth={
                                        props.grid.import && props.grid.import.previewWidth !== undefined ? props.grid.import.previewWidth : undefined
                                    }
                                    heights={props.grid.import && props.grid.import.heights !== undefined ? props.grid.import.heights : undefined}
                                    createable={props.grid.import && props.grid.import.createable !== undefined ? props.grid.import.createable : true}
                                    updateable={props.grid.import && props.grid.import.updateable !== undefined ? props.grid.import.updateable : true}
                                    defaultPageSize={props.grid.import ? props.grid.import.defaultPageSize : undefined}
                                    fields={props.fields}
                                    mapping={props.grid.import ? props.grid.import.mapping : []}
                                    aiEnabled={props.grid.import && props.grid.import.useAI === true}
                                    aiJobType={props.grid.import && props.grid.import.aiJobType}
                                    aiTargetTable={props.grid.import && props.grid.import.aiTargetTable}
                                    aiVariant={props.grid.import && props.grid.import.aiVariant}
                                    aiEntity={props.grid.import && props.grid.import.aiEntity}
                                    aiExtraInputData={props.grid.import && props.grid.import.aiExtraInputData}
                                    onImported={emptyOnImported}
                                />
                            )}
                            {opts.showDetailGridImport && props.detailGrid && (
                                <AntImport
                                    ref={importDetailGridPopup}
                                    API={props.detailGrid.API}
                                    table={props.detailGrid && props.detailGrid.table ? props.detailGrid.table : props.table}
                                    entityName={props.detailGrid.API.entityName ? props.detailGrid.API.entityName : props.entityName}
                                    entityDisplay={props.detailGrid.API.entityDisplay ? props.detailGrid.API.entityDisplay : props.entityDisplay}
                                    previewWidth={
                                        props.detailGrid.import && props.detailGrid.import.previewWidth !== undefined
                                            ? props.detailGrid.import.previewWidth
                                            : undefined
                                    }
                                    heights={
                                        props.detailGrid.import && props.detailGrid.import.heights !== undefined
                                            ? props.detailGrid.import.heights
                                            : undefined
                                    }
                                    createable={
                                        props.detailGrid.import && props.detailGrid.import.createable !== undefined
                                            ? props.detailGrid.import.createable
                                            : true
                                    }
                                    updateable={
                                        props.detailGrid.import && props.detailGrid.import.updateable !== undefined
                                            ? props.detailGrid.import.updateable
                                            : true
                                    }
                                    defaultPageSize={props.detailGrid.import ? props.detailGrid.import.defaultPageSize : undefined}
                                    fields={props.fields}
                                    mapping={props.detailGrid.import ? props.detailGrid.import.mapping : []}
                                    aiEnabled={props.detailGrid.import && props.detailGrid.import.useAI === true}
                                    aiJobType={props.detailGrid.import && props.detailGrid.import.aiJobType}
                                    aiTargetTable={props.detailGrid.import && props.detailGrid.import.aiTargetTable}
                                    aiVariant={props.detailGrid.import && props.detailGrid.import.aiVariant}
                                    aiEntity={props.detailGrid.import && props.detailGrid.import.aiEntity}
                                    aiExtraInputData={props.detailGrid.import && props.detailGrid.import.aiExtraInputData}
                                    onImported={emptyOnImported}
                                />
                            )}

                            {props.children}
                        </div>
                        </div>
                    )}
                </AntPage>
            )}
        </React.Fragment>
    );
});
